import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PitchAnalysisResponse } from "../../lib/api";
import { useApiClient } from "../../lib/api";

interface PitchListProps {
  jobId?: string;
  onEmptyStateCreate?: () => void;
  hideNewButton?: boolean;
}

const PitchList: React.FC<PitchListProps> = ({ jobId, onEmptyStateCreate, hideNewButton }) => {
  const navigate = useNavigate();
  const { api } = useApiClient();
  const [pitches, setPitches] = useState<PitchAnalysisResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [gradingPitches, setGradingPitches] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchPitches();
    // eslint-disable-next-line
  }, [jobId]);

  const fetchPitches = async () => {
    try {
      setLoading(true);
      let pitchData: PitchAnalysisResponse[] = [];
      if (jobId) {
        pitchData = await api.getPitchAnalysesByJob(jobId);
      } else {
        pitchData = await api.getPitchAnalyses();
      }
      setPitches(pitchData);
    } catch (error) {
      setPitches([]);
    } finally {
      setLoading(false);
    }
  };

  // Polling for grading pitches
  useEffect(() => {
    const gradingPitchIds = pitches.filter(pitch => pitch.status === 'grading').map(pitch => pitch.id);
    setGradingPitches(new Set(gradingPitchIds));
    if (gradingPitchIds.length > 0) {
      const interval = setInterval(fetchPitches, 5000);
      return () => clearInterval(interval);
    }
  }, [pitches]);

  // Helper: get grade badge
  const getStatusBadge = (pitch: PitchAnalysisResponse) => {
    if (pitch.status === 'grading') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 animate-pulse">
          Grading...
        </span>
      );
    }
    if (pitch.status === 'error') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">
          Error
        </span>
      );
    }
    if (pitch.status === 'ready' && pitch.overall_score !== undefined && pitch.overall_score !== null) {
      const score = pitch.overall_score;
      const letter = getScoreLetter(score);
      const colorMap: Record<string, string> = {
        S: 'bg-amber-300 text-amber-900',
        A: 'bg-green-300 text-green-900',
        B: 'bg-green-100 text-green-700',
        C: 'bg-yellow-100 text-yellow-700',
        D: 'bg-red-100 text-red-700',
        F: 'bg-red-200 text-red-900',
      };
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${colorMap[letter]}`}>{letter} ({score.toFixed(1)})</span>
      );
    }
    return null;
  };

  const getScoreLetter = (score: number | null | undefined) => {
    if (score == null) return '';
    if (score > 3.8) return 'S';
    if (score > 3.6) return 'A';
    if (score > 2.9) return 'B';
    if (score > 1.9) return 'C';
    if (score > 1.4) return 'D';
    return 'F';
  };

  // Render
  if (loading) {
    return (
      <div className="py-8 flex flex-col items-center">
        <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-2" />
        <div className="text-gray-500">Loading pitches...</div>
      </div>
    );
  }

  if (pitches.length === 0) {
    return (
      <div className="py-8 flex flex-col items-center">
        <div className="text-gray-500 mb-2">No pitches found{jobId ? ' for this job' : ''}.</div>

      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      {pitches.map((pitch) => {
        // Determine focus label
        let focusLabel = 'General';
        if (pitch.pitch_type === 'employment') focusLabel = 'Employment';
        else if (pitch.pitch_type === 'education') focusLabel = 'Education';

        // Grade logic (same as PitchAnalysis)
        const getScoreLetter = (score) => {
          if (score == null) return '-';
          if (score > 3.9) return 'S';
          if (score > 3.4) return 'A';
          if (score > 2.9) return 'B';
          if (score > 1.9) return 'C';
          if (score > 1.4) return 'D';
          return 'F';
        };
        const getGradeBg = (score) => {
          const letter = getScoreLetter(score);
          switch (letter) {
            case 'S': return 'bg-gradient-to-r from-amber-200 via-yellow-200 to-amber-300 border-amber-300';
            case 'A': return 'bg-green-100 border-green-300';
            case 'B': return 'bg-green-50 border-green-200';
            case 'C': return 'bg-yellow-50 border-yellow-200';
            case 'D': return 'bg-red-50 border-red-200';
            case 'F': return 'bg-red-200 border-red-400';
            default: return 'bg-white border-gray-200';
          }
        };
        const getGradeBadge = (score) => {
          const letter = getScoreLetter(score);
          switch (letter) {
            case 'S': return 'bg-amber-400 text-amber-900';
            case 'A': return 'bg-green-300 text-green-900';
            case 'B': return 'bg-green-200 text-green-700';
            case 'C': return 'bg-yellow-200 text-yellow-700';
            case 'D': return 'bg-red-200 text-red-700';
            case 'F': return 'bg-red-500 text-red-900';
            default: return 'bg-gray-100 text-gray-800';
          }
        };

        // Show grade badge if graded, otherwise status badge
        const isGraded = pitch.status === 'complete' && pitch.overall_score !== undefined && pitch.overall_score !== null;
        const gradeLetter = isGraded ? getScoreLetter(pitch.overall_score) : null;
        const gradeBg = isGraded ? getGradeBg(pitch.overall_score) : 'bg-white border-gray-200';
        const badgeClass = isGraded ? getGradeBadge(pitch.overall_score) : '';

        return (
          <div
            key={pitch.id}
            className={`rounded-xl shadow hover:shadow-lg transition cursor-pointer flex flex-col sm:flex-row items-stretch px-6 py-6 gap-4 min-h-[100px] ${gradeBg} border`}
            onClick={() => navigate(`/pitch/${pitch.id}${jobId ? `?jobId=${jobId}` : ''}`)}
          >
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs text-gray-500">
                  {pitch.created_at ? new Date(pitch.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                </span>
                <span className="inline-block bg-gray-100 text-gray-700 rounded px-2 py-0.5 text-xs font-medium">
                  {focusLabel}
                </span>
              </div>
              {pitch.content && pitch.content.trim() ? (
                <div className="italic text-gray-700 text-base leading-snug line-clamp-2 max-w-2xl">
                  {pitch.content}
                </div>
              ) : (
                <span className="italic text-gray-400">(No pitch text)</span>
              )}
            </div>
            <div className="flex flex-col items-end justify-center min-w-[90px]">
              {isGraded ? (
                <span className={`rounded-full px-5 py-2 text-lg font-bold shadow ${badgeClass}`}>{gradeLetter}</span>
              ) : (
                getStatusBadge(pitch)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PitchList;
